import curry from 'ramda/src/curry';

/**
 * Escape string
 *
 * Escape strings so they can safely be used with regex
 * Example: string `--` would change to `\-\-`
 *
 * @param {String} str - string that needs escaping
 *
 * @returns {String}
 */
const escapeString = (str: string) =>
    // eslint-disable-next-line no-useless-escape
    str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');

const constructMultiFindRegex = (findItems: any) =>
    new RegExp(findItems.join('|'), 'gi');

const constructPatternKeyMap = (
    keyMappings: any,
    patternStart: any,
    patternEnd: any
) =>
    Object.keys(keyMappings).reduce((result: any, key: string) => {
        const safeKey = escapeString(`${patternStart}${key}${patternEnd}`);
        result[safeKey] = keyMappings[key];
        return result;
    }, {});

const validPattern = (pattern: string) =>
    typeof pattern === 'string' && pattern.length > 0;

const stringByPatternReplacer = (
    patternStart: string,
    patternEnd: string,
    keyMappings: any,
    input: string
) => {
    if (!validPattern(patternStart) || !validPattern(patternEnd)) {
        return input;
    }

    if (!Object.keys(keyMappings).length) {
        return input;
    }

    const patternKeyMappings = constructPatternKeyMap(
        keyMappings,
        patternStart,
        patternEnd
    );
    const regex = constructMultiFindRegex(Object.keys(patternKeyMappings));
    const replaceMatchedValue = (matchedValue: string) => {
        const escapedValue = escapeString(matchedValue);
        return patternKeyMappings[escapedValue];
    };

    if (input && typeof input.replace === 'function') {
        return input.replace(regex, replaceMatchedValue);
    }

    return input;
};

const replaceStringWithPattern = curry(stringByPatternReplacer)('%{', '}');

export {
    replaceStringWithPattern,
    stringByPatternReplacer,
    constructMultiFindRegex,
    constructPatternKeyMap,
    escapeString,
    validPattern
};
